/**
 * Looking at a picture somebody sent, without leaving Pugo.
 *
 * Attachments used to open in the system browser, which throws you out of the
 * app to look at a screenshot. This is the same picture, over the app, with
 * the two things you actually want to do to it: copy it, or save it.
 *
 * Arrow keys move through everything shared in the same message, so a batch
 * of screenshots is one viewer rather than five round trips.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBackdropClose } from '@/lib/backdrop';
import { createPortal } from 'react-dom';
import type { Attachment } from '@/lib/types';
import { fileUrl } from '@/lib/api';
import { Zoomable } from './Zoomable';
import { cn } from '@/lib/cn';
import { copyText } from '@/lib/clipboard';
import { openExternal } from '@/lib/bridge';
import { Icon } from '@/icons/Icon';
import { useToast } from './ui';
import { Tip } from './HoverCard';

export function Lightbox({ list, index, onClose }: {
  list: Attachment[];
  index: number;
  onClose: () => void;
}) {
  const backdrop = useBackdropClose(onClose);
  const [at, setAt] = useState(index);
  const toast = useToast();
  const item = list[at];

  const step = useCallback((by: number) => {
    setAt((i) => (i + by + list.length) % list.length);
  }, [list.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, step]);

  if (!item) return null;
  const src = fileUrl(item.url);

  /**
   * Copying the picture itself where the browser allows it, and the address
   * where it does not - which is anywhere Pugo is not on HTTPS.
   */
  async function copyPicture() {
    try {
      if (navigator.clipboard && 'write' in navigator.clipboard && window.ClipboardItem) {
        const blob = await (await fetch(src)).blob();
        await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
        toast('Picture copied', 'good');
        return;
      }
    } catch {
      // Not permitted here; fall back to the link.
    }
    const ok = await copyText(src);
    toast(ok ? 'Link copied' : 'Could not copy', ok ? 'good' : 'bad');
  }

  /** Saves it. In the desktop app this hands off to the normal download. */
  function save() {
    const a = document.createElement('a');
    a.href = src;
    a.download = item!.name || 'image';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return createPortal(
    <div
      data-keeps-card
      className="lightbox-in fixed inset-0 z-[500] flex flex-col bg-[rgba(3,5,10,.9)] backdrop-blur-[6px]"
      // Anywhere off the picture closes it. Testing only the backdrop element
      // meant the empty space around the image did nothing, which is exactly
      // where people click to dismiss.
      {...backdrop}
    >
      {/* Only the way out, up here. Everything else belongs with the picture. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute top-4 right-4 z-[2] grid h-9 w-9 place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-white/10 text-white/80 backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-white"
      >
        <Icon name="close" size={17} />
      </button>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-4 pt-14"
        {...backdrop}
      >
        {list.length > 1 ? <LightArrow side="left" onClick={() => step(-1)} /> : null}

        {/* Pinch, wheel and double-tap all zoom in here. Everywhere else in
            the app zoom is blocked - a picture is the exception. */}
        <Zoomable src={src} alt={item.name} onSwipe={(dir) => step(dir)} onBackground={onClose} />

        {list.length > 1 ? <LightArrow side="right" onClick={() => step(1)} /> : null}
      </div>
      <style>{`.lightbox-in { animation: lightbox-in 240ms ease-out both } @keyframes lightbox-in { from { opacity: 0 } to { opacity: 1 } } @media (prefers-reduced-motion: reduce) { .lightbox-in { animation: none } }`}</style>

      {/* What it is and what you can do with it, under the picture where the
          eye already is rather than in a bar you have to look up for. */}
      <div className="flex flex-none items-center justify-center gap-2 px-4 py-4">
        <div className="flex items-center gap-2 rounded-[calc(14px*var(--radius-mul,1))] bg-white/[.07] px-3 py-2 backdrop-blur-sm">
          <span className="min-w-0 pr-1">
            <span className="block max-w-[260px] truncate text-[12.5px] font-semibold text-white">{item.name}</span>
            <span className="block text-[11px] text-white/50">
              {readableSize(item.size)}
              {list.length > 1 ? ' · ' + (at + 1) + ' of ' + list.length : ''}
            </span>
          </span>
          <span className="h-7 w-px bg-white/15" />
          <LightBtn label="Copy" icon="copy" onClick={() => void copyPicture()} />
          <LightBtn label="Save" icon="download" onClick={save} />
          <LightBtn label="Open outside Pugo" icon="external" onClick={() => openExternal(src)} />
        </div>
      </div>
    </div>,
    document.body
  );
}

function LightBtn({ label, icon, onClick }: { label: string; icon: string; onClick: () => void }) {
  return (
    <Tip text={label}>
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-white/10 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
    >
      <Icon name={icon} size={16} />
    </button>
    </Tip>
  );
}

function LightArrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Previous' : 'Next'}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white/80',
        'transition-colors hover:bg-white/20 hover:text-white',
        side === 'left' ? 'left-4' : 'right-4'
      )}
    >
      <Icon name={side === 'left' ? 'chevronL' : 'chevronR'} size={20} />
    </button>
  );
}

function readableSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
